import { useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MemberRole } from "@kandoo/shared";
import { api } from "../api/client";
import { ConfirmButton } from "../components/ConfirmButton";

export function OrgsPage() {
  const qc = useQueryClient();
  const orgsQ = useQuery({ queryKey: ["orgs"], queryFn: () => api.orgs.list() });

  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => api.orgs.create({ name: name.trim() }),
    onSuccess: (org) => {
      qc.invalidateQueries({ queryKey: ["orgs"] });
      setName("");
      setSelected(org.id);
    },
  });

  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">Organizations</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-[var(--surface)] p-4"
      >
        <label className="flex-1 text-sm">
          <span className="text-slate-600">New organization</span>
          <input
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Inc."
          />
        </label>
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Create
        </button>
      </form>

      {orgsQ.isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (orgsQ.data ?? []).length === 0 ? (
        <p className="text-slate-500">
          No organizations yet — create one to share a set of boards with a team.
        </p>
      ) : (
        <ul className="space-y-2">
          {(orgsQ.data ?? []).map((o) => (
            <li
              key={o.id}
              className="rounded-lg border border-slate-200 bg-[var(--surface)]"
            >
              <button
                type="button"
                onClick={() => setSelected(selected === o.id ? null : o.id)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <span className="font-medium text-slate-800">{o.name}</span>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="capitalize">{o.role}</span>
                  {selected === o.id ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </span>
              </button>
              {selected === o.id && (
                <div className="border-t border-slate-100 p-4">
                  <OrgMembers orgId={o.id} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function OrgMembers({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const orgQ = useQuery({
    queryKey: ["org", orgId],
    queryFn: () => api.orgs.get(orgId),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["org", orgId] });
    qc.invalidateQueries({ queryKey: ["orgs"] });
  };

  const invite = useMutation({
    mutationFn: (b: { username: string; role: MemberRole }) =>
      api.orgs.invite(orgId, b),
    onSuccess: invalidate,
  });
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      api.orgs.setRole(orgId, userId, role),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.orgs.removeMember(orgId, userId),
    onSuccess: invalidate,
  });

  const [username, setUsername] = useState("");
  const [role, setRoleValue] = useState<MemberRole>("editor");

  if (orgQ.isLoading || !orgQ.data)
    return <p className="text-sm text-slate-500">Loading members…</p>;

  const isOwner = orgQ.data.role === "owner";
  const members = orgQ.data.members;

  return (
    <div>
      {isOwner && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const u = username.trim();
            if (u) invite.mutate({ username: u, role }, { onSuccess: () => setUsername("") });
          }}
          className="mb-3 flex flex-wrap gap-2"
        >
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Invite by username"
            className="min-w-40 flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-blue-400"
          />
          <select
            value={role}
            onChange={(e) => setRoleValue(e.target.value as MemberRole)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            type="submit"
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            Add
          </button>
        </form>
      )}
      {invite.isError && (
        <p className="mb-2 text-sm text-red-600">
          {(invite.error as Error).message}
        </p>
      )}

      <ul className="divide-y divide-slate-100">
        {members.map((m) => (
          <li key={m.userId} className="flex items-center gap-3 py-2">
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800">
                {m.displayName || m.username}
              </div>
              <div className="text-xs text-slate-500">@{m.username}</div>
            </div>
            {m.isOwner ? (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                Owner
              </span>
            ) : isOwner ? (
              <>
                <select
                  value={m.role}
                  onChange={(e) =>
                    setRole.mutate({
                      userId: m.userId,
                      role: e.target.value as MemberRole,
                    })
                  }
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                >
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <ConfirmButton
                  onConfirm={() => remove.mutate(m.userId)}
                  title="Remove member"
                  className="rounded px-1.5 py-1 text-xs text-slate-400 hover:bg-red-50 hover:text-red-600"
                  confirmClassName="rounded bg-red-600 px-1.5 text-xs text-white"
                  confirmLabel="Remove?"
                >
                  <X size={14} />
                </ConfirmButton>
              </>
            ) : (
              <span className="text-xs capitalize text-slate-500">{m.role}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
